
import React, { Component, ErrorInfo, ReactNode } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Shield, RefreshCw, AlertTriangle, Home } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface Props {
  children: ReactNode;
  showGoHome?: boolean;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class AuthErrorBoundaryClass extends Component<Props & { navigate?: (path: string) => void }, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // You could send to Sentry or other log here
    console.error("[AuthErrorBoundary]", error, errorInfo);
    this.setState({ errorInfo });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };
  handleGoHome = () => {
    if (this.props.navigate) {
      this.props.navigate("/");
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="space-y-4 max-w-md mx-auto my-8">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>Authentication Error:</strong> An unexpected error occurred during the authentication process.
            </AlertDescription>
          </Alert>
          <Card className="border-red-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-800">
                <Shield className="h-5 w-5" />
                Auth Error
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-red-700">
                The authentication system ran into an unexpected error.<br />
                Please try again, or if the issue persists, contact support.
              </p>
              {this.state.error && (
                <details className="text-sm">
                  <summary className="cursor-pointer font-medium text-red-600">Technical details</summary>
                  <div className="mt-2 p-3 bg-red-50 rounded border">
                    <p><strong>Error:</strong> {this.state.error.message}</p>
                    {this.state.errorInfo && (
                      <pre className="mt-2 text-xs whitespace-pre-wrap break-words text-red-600">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    )}
                  </div>
                </details>
              )}
              <div className="flex gap-2">
                <Button onClick={this.handleRetry} variant="outline">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Retry
                </Button>
                {this.props.showGoHome && (
                  <Button onClick={this.handleGoHome} variant="secondary">
                    <Home className="h-4 w-4 mr-2" />
                    Home
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }
    return this.props.children;
  }
}

// Wrapper to inject navigate
const AuthErrorBoundary: React.FC<Props> = ({ children, showGoHome = false }) => {
  const navigate = useNavigate();
  return (
    <AuthErrorBoundaryClass navigate={navigate} showGoHome={showGoHome}>
      {children}
    </AuthErrorBoundaryClass>
  );
};

export default AuthErrorBoundary;
